import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { sitesApi } from "../lib/sites";
import { usersApi } from "../lib/users";
import { usageApi } from "../lib/usage";
import { siteControllersApi } from "../lib/site-controllers";
import { areasApi } from "../lib/areas";
import SitesControl from "../components/sites/SitesControl";
import SiteControllerStateBadge from "../components/site-controllers/SiteControllerStateBadge";
import PageBrandingLogo from "../components/PageBrandingLogo";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Users, BarChart3 } from "lucide-react";

// Main Dashboard Component
export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [uiAlert, setUiAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({ open: false, title: "Notice", message: "" });

  const isSuperUser = user?.role === "super_user";
  const isSiteAdmin = user?.role === "site_admin";
  const goToSites = () => navigate("/sites");
  const goToUsers = () => navigate("/users");
  const goToUsage = () => navigate("/usage");

  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.getAll(),
    enabled: !!user,
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => usersApi.getAll(),
    enabled: !!user && (isSuperUser || isSiteAdmin),
  });

  const usageLastSevenDays = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 7 * 24 * 60 * 60 * 1000);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }, []);

  const { data: aggregatedUsage, isLoading: usageLoading } = useQuery({
    queryKey: [
      "usage",
      "aggregated",
      "dashboard",
      user?.id,
      isSuperUser ? "super_user" : isSiteAdmin ? "site_admin" : "other",
      usageLastSevenDays.startDate,
      usageLastSevenDays.endDate,
    ],
    queryFn: () =>
      usageApi.getAggregated({
        groupBy: "day",
        startDate: usageLastSevenDays.startDate,
        endDate: usageLastSevenDays.endDate,
      }),
    enabled: !!user && (isSuperUser || isSiteAdmin),
  });

  const { data: allControllers, isLoading: controllersLoading } = useQuery({
    queryKey: ["all-controllers", sites?.map((s) => s.id)],
    queryFn: async () => {
      if (!sites || sites.length === 0) return [];
      const controllerPromises = sites.map((site) =>
        siteControllersApi.getBySite(site.id).then((controllers) => ({
          siteId: site.id,
          siteName: site.name,
          controllers,
        }))
      );
      return await Promise.all(controllerPromises);
    },
    enabled: !!user && !!sites && sites.length > 0,
  });

  const setCurrentPresetMutation = useMutation({
    mutationFn: ({ areaId, presetId }: { areaId: string; presetId: string }) =>
      areasApi.setCurrentPreset(areaId, presetId),
    onSuccess: (updatedArea) => {
      queryClient.invalidateQueries({
        queryKey: ["areas", updatedArea.siteControllerId],
      });
      queryClient.invalidateQueries({ queryKey: ["area", updatedArea.id] });
      queryClient.invalidateQueries({ queryKey: ["presets", updatedArea.id] });
    },
    onError: (error: unknown) => {
      const err = error as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to set current preset";
      setUiAlert({ open: true, title: "Error", message: errorMessage });
    },
  });

  const handleSetCurrentPreset = (
    areaId: string,
    presetId: string | null,
    _presetAddress?: number | null
  ) => {
    if (presetId === null) return;
    setCurrentPresetMutation.mutate({ areaId, presetId });
  };

  if (!user) {
    return (
      <div className="px-2 py-3 sm:px-0">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (
    sitesLoading ||
    usageLoading ||
    controllersLoading ||
    (usersLoading && (isSuperUser || isSiteAdmin))
  ) {
    return (
      <div className="px-2 py-3 sm:px-0">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  const usageData = aggregatedUsage || [];
  const totalUsageSeconds = usageData.reduce(
    (acc, item) => acc + item.totalDuration,
    0
  );
  const totalUsageHours = Math.floor(totalUsageSeconds / 3600);
  const totalUsageMinutes = Math.round((totalUsageSeconds - (totalUsageHours * 3600)) / 60);
  const totalSites = sites?.length || 0;
  // Match UsersPage visibility: site admins only see end users in their organisation.
  const visibleUsers = isSiteAdmin
    ? users?.filter((u) => {
        if (u.role !== "end_user") return false;
        if (!user.organisationId) return true;
        return u.organisationId === user.organisationId;
      })
    : users;
  const totalUsers = visibleUsers?.length || 0;

  const controllerList =
    allControllers?.flatMap((entry) => entry.controllers) || [];

  return (
    <>
    <div className="px-2 py-3 sm:px-0">
      <div className="flex flex-row justify-between items-start gap-4 mb-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">
            {isSuperUser
              ? "System Dashboard"
              : isSiteAdmin
              ? "My Sites Dashboard"
              : "My Dashboard"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSuperUser
              ? "Manage all sites, users, and system configuration"
              : isSiteAdmin
              ? "Manage end users and groups for your assigned sites"
              : "Control lights in your accessible areas and view your usage statistics"}
          </p>
        </div>
        <PageBrandingLogo />
      </div>

      {/* Statistics Cards */}
      {(isSuperUser || isSiteAdmin) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {/* Sites Card */}
          {(isSuperUser || isSiteAdmin) && (
            <Card
              onClick={goToSites}
              className="cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xl font-bold">{totalSites} Sites</p>
                    <SiteControllerStateBadge
                      className="mt-2"
                      controllers={controllerList}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Building2 className="h-8 w-8 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Users Card */}
          {(isSuperUser || isSiteAdmin) && (
            <Card
              onClick={goToUsers}
              className="cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xl font-bold">{totalUsers} Users</p>
                  </div>
                  <Users className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
          )}
          {/* Usage (last 7 days): all sites for super user; assigned sites for site admin */}
          {(isSuperUser || isSiteAdmin) && (
            <Card
              onClick={goToUsage}
              className="cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xl font-bold">
                      {totalUsageHours.toString().padStart(2, '0')}:{totalUsageMinutes.toString().padStart(2, '0')}h Usage
                 
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Last 7 days{isSuperUser ? " · all sites" : " · your sites"}
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>
          )}
          {/* My Usage Card */}
          {/* {isEndUser && (
          <Card
            onClick={goToUsage}
            className="cursor-pointer hover:bg-muted/50 transition-colors"
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    My Total Usage
                  </p>
                  <p className="text-xl font-bold">{totalUsageHours} hours</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {usageData.length} day{usageData.length !== 1 ? "s" : ""}{" "}
                    tracked
                  </p>
                </div>
                <BarChart3 className="h-12 w-12 text-primary" />
              </div>
            </CardContent>
          </Card>
        )} */}
        </div>
      )}

      {/* Site Controllers / Control View */}
      {allControllers && (
        <SitesControl
          allControllers={allControllers}
          sites={sites}
          onSetCurrentPreset={handleSetCurrentPreset}
        />
      )}
    </div>
    <AlertDialog
      open={uiAlert.open}
      onOpenChange={(open) =>
        setUiAlert((prev) => ({ ...prev, open }))
      }
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
          <AlertDialogDescription>{uiAlert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
